import React, { Fragment, useEffect, useState } from 'react'
import {
  PlusCircle as PlusCircleIcon,
  Trash2 as Trash2Icon,
  UploadCloud as UploadCloudIcon,
} from 'react-feather'
import { useFormContext, useFieldArray } from 'react-hook-form'
import clsx from 'clsx'
import moment from 'moment'
import { size } from 'lodash'
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
} from '@material-ui/core'

import { AlertDialog, LinkedDocumentModal } from 'components'
import { DetailsPlanForm, SummaryFormItems } from './components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const SAVE_BEFORE_FINALIZE_MESSAGE =
  'Salve a atividade antes de concluir um plano de ação desta evidência.'

const EvidenceQuestion = ({
  evidencePlanActionId,
  evidencePlanAction,
  users,
  componentIndex,
  governanceProject,
  governanceProjectId,
  planActionsOpened,
  setStates,
  disabledInputs,
  activity,
  hasAnswer = false,
  excludedIds = [],
}) => {
  const { CONCLUSION_STATUS_ID, HEADWAY_STATUS_ID } = constants.planAction
  const { EVIDENCE_FILE_TYPES, EVIDENCE_FILE_MIMES_TYPES } =
    constants.governanceProject

  const {
    setLoading,
    setPlanActionsOpened,
    setDefaultValues,
    setModifiedForm,
  } = setStates

  const name = `evidencePlanActions[${componentIndex}]`

  const planActions = evidencePlanAction?.planActions || []

  const [openDialog, setOpenDialog] = useState(false)
  const [openLinkedModal, setOpenLinkedModal] = useState(false)
  const [planActionData, setPlanActionData] = useState({})
  const [expandedAccordions, setExpandedAccordions] = useState([])

  const [responsibles, setResponsibles] = useState({})

  const snackbar = useSnackbar()
  const classes = useStyles()

  const {
    control,
    errors,
    watch,
    setValue,
    triggerValidation,
    getValues,
    reset,
  } = useFormContext()
  const { fields, append } = useFieldArray({
    control,
    name: `${name}.planActions`,
  })

  const activityDeadlineDate = watch('deadlineDate')

  const componentError = errors?.evidencePlanActions?.[componentIndex]

  const handleOpenLinkedModal = () => setOpenLinkedModal(true)

  const handleLinkedDocumentSubmit = (attachments, privacyPolicyIds) => {
    const evidencePath = `evidencePlanActions[${componentIndex}].evidence`
    const currentAttachments = getValues(`${evidencePath}.attachments`) || []
    const currentPrivacyPolicyIds =
      getValues(`${evidencePath}.privacyPolicyIds`) || []

    const newAttachments = helpers.file.filterExisting(
      attachments,
      currentAttachments,
    )
    const newPrivacyPolicyIds = helpers.file.filterExistingDocuments(
      privacyPolicyIds,
      currentPrivacyPolicyIds,
    )

    setValue(`${evidencePath}.attachments`, [
      ...helpers.file.toArray(currentAttachments),
      ...(newAttachments || []),
    ])
    setValue(`${evidencePath}.privacyPolicyIds`, [
      ...currentPrivacyPolicyIds,
      ...newPrivacyPolicyIds,
    ])
  }

  const handleAddPlanAction = () => {
    append({
      responsible: { id: null, name: '' },
      deadlineDate: activityDeadlineDate || null,
      what: '',
      how: '',
      because: '',
      where: '',
      howPrice: '',
      status: '',
    })
  }

  const handleOpenDialog = () => setOpenDialog(true)
  const handleCloseDialog = () => {
    setPlanActionData({})
    setOpenDialog(false)
  }

  const handleAccordionChange = (accordionId) => (event, isExpanded) => {
    setExpandedAccordions((prev) =>
      isExpanded
        ? [...prev, accordionId]
        : prev.filter((id) => id !== accordionId),
    )
  }

  const handleRemovePlanActionSafely = (index) => {
    const formValues = getValues()
    const structured = helpers.functions.unflattenObject(formValues)
    const structuredEvidencePlanActions =
      structured?.evidencePlanActions?.[componentIndex]

    const currentEvidenceQuestion = {
      title: evidencePlanAction?.title,
      description: evidencePlanAction?.description,
      ...(structuredEvidencePlanActions ?? {}),
    }

    const newPlanActions = (currentEvidenceQuestion?.planActions || []).filter(
      (_, i) => i !== index,
    )

    delete currentEvidenceQuestion.planActions

    const newEvidenceQuestion = {
      ...currentEvidenceQuestion,
      planActions: newPlanActions,
    }

    const newEvidencePlanActions = [...structured.evidencePlanActions]

    newEvidencePlanActions[componentIndex] = newEvidenceQuestion

    // Utilizamos o reset do formulário aqui porque o React Hook Form,
    // na versão atual, não remove corretamente campos dinâmicos aninhados
    // (como planActions) apenas com setValue ou remove.
    // O reset garante que o estado do formulário seja reconstruído sem os
    // campos removidos, evitando inconsistências e dados órfãos.
    reset({
      ...structured,
      evidencePlanActions: newEvidencePlanActions,
    })
  }

  const handleValidationPlanAction = (index, originId) => {
    if (originId) {
      setPlanActionData({ index, originId })
      handleOpenDialog()
    } else {
      handleRemovePlanActionSafely(index)
    }
  }

  const handleChangeResponsibles = (user, index) => {
    setResponsibles((prev) => ({ ...prev, [index]: user }))
    setValue(`${name}.planActions.${index}.responsible`, {
      id: user?.id ?? null,
      name: user?.name ?? '',
    })
  }

  const handleDeletePlanAction = async () => {
    try {
      setLoading(true)

      await service.dponet.governanceProject.deletePlanAction({
        governanceProjectId,
        planActionId: planActionData?.originId,
      })

      snackbar.open({
        message: 'Plano de ação excluído com sucesso!',
        variant: 'success',
      })
      handleRemovePlanActionSafely(planActionData?.index)
      handleCloseDialog()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReopenPlanAction = async (event, originId) => {
    event.stopPropagation()

    try {
      setLoading(true)

      await service.dponet.governanceProject.changeStatusPlanAction({
        governanceProjectId,
        planActionId: originId,
        planAction: {
          status: HEADWAY_STATUS_ID,
        },
      })

      snackbar.open({
        message: 'Plano de ação reaberto com sucesso!',
        variant: 'success',
      })
      setModifiedForm(true)
      setPlanActionsOpened((prev) => [...prev, originId])
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFinalizePlanAction = async (index) => {
    if (!(await triggerValidation(`${name}.planActions[${index}]`))) return

    // O payload de conclusão só carrega ids — sem o da evidence_plan_action o backend entenderia a
    // chamada como criação de uma evidência nova, sem nome nem descrição (que não viajam aqui), e
    // recusaria com uma mensagem que não tem relação com o botão clicado.
    if (!evidencePlanActionId) {
      return snackbar.open({
        message: SAVE_BEFORE_FINALIZE_MESSAGE,
        variant: 'error',
      })
    }

    try {
      setLoading(true)

      const formValues = getValues()
      const structured = helpers.functions.unflattenObject(formValues)

      const evidencePlanAction =
        structured?.evidencePlanActions?.[componentIndex]

      const planAction = evidencePlanAction?.planActions[index]

      // Mesmo motivo do id da evidência: sem originId o backend cria um plano de ação novo em vez de
      // concluir o que está na tela, e o faria já com status de concluído.
      if (!planAction?.originId) {
        return snackbar.open({
          message: SAVE_BEFORE_FINALIZE_MESSAGE,
          variant: 'error',
        })
      }

      if (!planAction?.responsible?.id) {
        return snackbar.open({
          message: 'O campo Responsável é obrigatório.',
          variant: 'error',
        })
      }

      const evidencePlanActions = [
        {
          id: evidencePlanActionId,
          planActions: [
            {
              id: planAction?.originId,
              responsibleUserId: planAction?.responsible?.id,
              deadlineDate: moment(planAction?.deadlineDate).toISOString(),
              what: planAction?.what,
              how: planAction?.how,
              because: planAction?.because,
              where: planAction?.where,
              howPrice: planAction?.howPrice,
              status: CONCLUSION_STATUS_ID,
            },
          ],
        },
      ]

      await service.dponet.governanceProject.answerPlanAction({
        governanceProjectId,
        governanceProjectActivityId: activity?.id,
        evidencePlanActions,
      })

      snackbar.open({
        message: 'Plano de ação concluído com sucesso!',
        variant: 'success',
      })

      setDefaultValues((prevValues) => {
        const newValues = { ...prevValues }

        if (
          newValues?.evidencePlanActions?.[componentIndex]?.planActions[index]
        ) {
          newValues.evidencePlanActions[componentIndex].planActions[index] = {
            ...newValues.evidencePlanActions[componentIndex].planActions[index],
            responsibleUserId: planAction?.responsible?.id,
            deadlineDate: moment(planAction?.deadlineDate).toISOString(),
            what: planAction?.what,
            how: planAction?.how,
            because: planAction?.because,
            where: planAction?.where,
            howPrice: planAction?.howPrice,
          }
        }

        return newValues
      })

      setPlanActionsOpened((prev) =>
        prev?.filter((id) => id !== planAction?.originId),
      )
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (size(fields) > 0) {
      const initialResponsibles = fields.reduce((acc, planAction, idx) => {
        acc[idx] = {
          id: planAction?.responsible?.id ?? null,
          name: planAction?.responsible?.name ?? '',
        }
        return acc
      }, {})
      setResponsibles(initialResponsibles)
    }
  }, [fields])

  /**
   * Este efeito garante que, ao renderizar os campos dinâmicos de planos de ação,
   * o campo 'deadlineDate' de cada plano seja preenchido automaticamente com o prazo da atividade,
   * caso ainda não esteja definido. Isso é necessário porque o React Hook Form (v5) só aplica o
   * defaultValue na primeira montagem do campo, e campos criados dinamicamente após a resposta
   * (answer) não recebem esse valor automaticamente. Assim, evitamos que o campo de data fique vazio
   * quando o usuário responde a questão e os planos de ação são exibidos.
   */
  useEffect(() => {
    if (!hasAnswer && planActions && activityDeadlineDate) {
      planActions.forEach((planAction, idx) => {
        if (!planAction?.deadlineDate) {
          setValue(
            `${name}.planActions.${idx}.deadlineDate`,
            activityDeadlineDate,
            { shouldValidate: true },
          )
        }
      })
    }
    //eslint-disable-next-line
  }, [])

  return (
    <Fragment>
      {!disabledInputs && (
        <Box>
          <Box className={classes.attachmentButtonContainer}>
            <Box display="flex" flexDirection="column" width="100%">
              <Button
                color="primary"
                variant="contained"
                startIcon={<UploadCloudIcon />}
                className={clsx(classes.attachmentButton, {
                  [classes.errorButton]:
                    !!componentError?.evidence?.attachments,
                })}
                onClick={handleOpenLinkedModal}
                disabled={disabledInputs}
                fullWidth
              >
                Selecionar Arquivos
              </Button>
            </Box>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<PlusCircleIcon />}
              className={clsx(
                classes.attachmentButton,
                classes.planActionButton,
              )}
              onClick={handleAddPlanAction}
              disabled={disabledInputs}
              fullWidth
            >
              Adicionar plano de ação
            </Button>
          </Box>
          {!!componentError?.evidence?.attachments && (
            <FormHelperText error={!!componentError?.evidence?.attachments}>
              {<>{componentError?.evidence?.attachments?.message}</>}
            </FormHelperText>
          )}
        </Box>
      )}
      {/* Card de plano de ação */}
      {fields?.map((planAction, index) =>
        !planActionsOpened.includes(planAction?.originId) &&
        !!planAction?.status ? (
          <Accordion
            className={classes.accordion}
            key={planAction?.id}
            onChange={handleAccordionChange(`plan-action-${index}`)}
            expanded={expandedAccordions.includes(`plan-action-${index}`)}
          >
            <AccordionSummary id={`plan-action-header-${index}`}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(4)}
                width="100%"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography variant="h4">Plano de ação</Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={theme.spacing(1)}
                  >
                    {!disabledInputs && (
                      <Tooltip title="Reabrir plano de ação">
                        <Box>
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={disabledInputs}
                            onClick={(event) =>
                              handleReopenPlanAction(
                                event,
                                planAction?.originId,
                              )
                            }
                          >
                            Reabrir
                          </Button>
                        </Box>
                      </Tooltip>
                    )}
                    <IconButton>
                      <ExpandMoreIcon
                        className={clsx({
                          [classes.iconTransition]: true,
                          [classes.rotateIcon]: expandedAccordions.includes(
                            `plan-action-${index}`,
                          ),
                        })}
                      />
                    </IconButton>
                  </Box>
                </Box>
                <Grid container spacing={3}>
                  <SummaryFormItems
                    name={name}
                    index={index}
                    users={users}
                    planAction={planAction}
                    governanceProject={governanceProject}
                    componentIndex={componentIndex}
                    disabled
                    responsibleValue={responsibles[index]}
                    setResponsibleValue={(user) =>
                      handleChangeResponsibles(user, index)
                    }
                  />
                </Grid>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <DetailsPlanForm
                  name={name}
                  users={users}
                  index={index}
                  componentIndex={componentIndex}
                  disabled
                />
              </Grid>
            </AccordionDetails>
          </Accordion>
        ) : (
          <Paper key={planAction?.id} className={classes.planActionPaper}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              minHeight={60}
            >
              <Typography variant="h4">Plano de ação</Typography>
              {!disabledInputs && (
                <IconButton
                  disabled={disabledInputs}
                  onClick={() =>
                    handleValidationPlanAction(index, planAction?.originId)
                  }
                >
                  <Trash2Icon size={20} />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={3}>
              <SummaryFormItems
                name={name}
                users={users}
                index={index}
                planAction={planAction}
                governanceProject={governanceProject}
                componentIndex={componentIndex}
                disabled={disabledInputs}
                responsibleValue={responsibles[index]}
                setResponsibleValue={(user) =>
                  handleChangeResponsibles(user, index)
                }
              />
              <DetailsPlanForm
                name={name}
                users={users}
                index={index}
                componentIndex={componentIndex}
                disabled={disabledInputs}
              />
              {!disabledInputs && !!planAction?.status && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleFinalizePlanAction(index)}
                    >
                      Concluir plano de ação
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        ),
      )}
      {openDialog && (
        <AlertDialog
          open={openDialog}
          onClose={handleCloseDialog}
          variant="standard"
          severity="warning"
          content="Tem certeza de que deseja excluir este plano de ação? Essa ação não poderá ser desfeita!"
          dialogActions={
            <>
              <Button
                variant="outlined"
                color="primary"
                className={classes.outlinedWarningButton}
                onClick={handleCloseDialog}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.containerWarningButton}
                onClick={handleDeletePlanAction}
              >
                Excluir
              </Button>
            </>
          }
        />
      )}
      {openLinkedModal && (
        <LinkedDocumentModal
          open={openLinkedModal}
          setOpen={setOpenLinkedModal}
          disabledInputs={disabledInputs}
          excludedIds={excludedIds}
          onSubmit={handleLinkedDocumentSubmit}
          accept={EVIDENCE_FILE_TYPES}
          mimeTypes={EVIDENCE_FILE_MIMES_TYPES}
        />
      )}
    </Fragment>
  )
}

export default EvidenceQuestion
